import { parsePageParam, parsePositiveId, TASK_LIST_PAGE_SIZE } from "./taskListPagination";

export const VALID_STATUSES = ["todo", "in_progress", "done", "cancelled"];
export const VALID_PRIORITIES = ["low", "medium", "high"];

export const STATUS_CHIPS = [
  { value: "todo", label: "До виконання" },
  { value: "in_progress", label: "В роботі" },
  { value: "done", label: "Виконано" },
  { value: "cancelled", label: "Скасовано" },
];

export const PRIORITY_CHIPS = [
  { value: "low", label: "Низький" },
  { value: "medium", label: "Середній" },
  { value: "high", label: "Високий" },
];

const normalizeEnumList = (values, allowed) => {
  const seen = new Set();
  const result = [];
  for (const raw of values) {
    const value = String(raw);
    if (!allowed.includes(value) || seen.has(value)) continue;
    seen.add(value);
    result.push(value);
  }
  return result;
};

export const parseIsoInstantParam = (raw) => {
  if (raw == null || raw === "") {
    return { iso: null, invalid: false, raw: null };
  }
  const value = String(raw);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return { iso: null, invalid: true, raw: value };
  }
  return { iso: parsed.toISOString(), invalid: false, raw: value };
};

export const isoFromDatetimeLocal = (localValue) => {
  if (!localValue) return null;
  const d = new Date(localValue);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

export const datetimeLocalFromIso = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (value) => String(value).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const isReversedDateRange = (dueAfterIso, dueBeforeIso) => {
  if (!dueAfterIso || !dueBeforeIso) return false;
  const after = new Date(dueAfterIso);
  const before = new Date(dueBeforeIso);
  if (Number.isNaN(after.getTime()) || Number.isNaN(before.getTime())) return false;
  return after.getTime() > before.getTime();
};

export const parseFiltersFromSearchParams = (searchParams) => {
  const page = parsePageParam(searchParams.get("page"));
  const statuses = normalizeEnumList(searchParams.getAll("status"), VALID_STATUSES);
  const priorities = normalizeEnumList(searchParams.getAll("priority"), VALID_PRIORITIES);
  const overdue = searchParams.get("overdue") === "1";
  const dueAfter = parseIsoInstantParam(searchParams.get("due_after"));
  const dueBefore = parseIsoInstantParam(searchParams.get("due_before"));
  const clientId = parsePositiveId(searchParams.get("client"));
  const propertyId = parsePositiveId(searchParams.get("property"));
  const dueAfterIso = dueAfter.iso;
  const dueBeforeIso = dueBefore.iso;
  const invalidDateParams = dueAfter.invalid || dueBefore.invalid;

  return {
    page,
    statuses,
    priorities,
    overdue,
    dueAfterIso,
    dueBeforeIso,
    dueAfterInvalid: dueAfter.invalid,
    dueBeforeInvalid: dueBefore.invalid,
    invalidDateParams,
    clientId,
    propertyId,
    reversedDateRange:
      !invalidDateParams && isReversedDateRange(dueAfterIso, dueBeforeIso),
  };
};

export const hasActiveFilters = (filters) => {
  if (!filters) return false;
  return (
    (filters.statuses && filters.statuses.length > 0) ||
    (filters.priorities && filters.priorities.length > 0) ||
    Boolean(filters.overdue) ||
    Boolean(filters.dueAfterIso) ||
    Boolean(filters.dueBeforeIso) ||
    Boolean(filters.invalidDateParams) ||
    filters.clientId != null ||
    filters.propertyId != null
  );
};

export const buildApiParams = (filters) => {
  const params = {
    page: filters.page,
    page_size: TASK_LIST_PAGE_SIZE,
  };

  if (filters.statuses.length > 0) {
    params.status = filters.statuses;
  }
  if (filters.priorities.length > 0) {
    params.priority = filters.priorities;
  }
  if (filters.overdue) {
    params.is_overdue = true;
  }
  if (filters.clientId != null) {
    params.client = filters.clientId;
  }
  if (filters.propertyId != null) {
    params.property = filters.propertyId;
  }

  if (!filters.reversedDateRange && !filters.invalidDateParams) {
    if (filters.dueAfterIso) {
      params.due_after = filters.dueAfterIso;
    }
    if (filters.dueBeforeIso) {
      params.due_before = filters.dueBeforeIso;
    }
  }

  return params;
};

export const buildUrlSearchParams = (filters) => {
  const next = new URLSearchParams();

  if (filters.page > 1) {
    next.set("page", String(filters.page));
  }

  filters.statuses.forEach((status) => {
    next.append("status", status);
  });
  filters.priorities.forEach((priority) => {
    next.append("priority", priority);
  });

  if (filters.overdue) {
    next.set("overdue", "1");
  }
  if (filters.dueAfterIso) {
    next.set("due_after", filters.dueAfterIso);
  }
  if (filters.dueBeforeIso) {
    next.set("due_before", filters.dueBeforeIso);
  }
  if (filters.clientId != null) {
    next.set("client", String(filters.clientId));
  }
  if (filters.propertyId != null) {
    next.set("property", String(filters.propertyId));
  }

  return next;
};

export const filtersWithPageReset = (filters, patch) => ({
  ...filters,
  ...patch,
  page: 1,
});

export const toggleSetValue = (values, value) => {
  const set = new Set(values);
  if (set.has(value)) {
    set.delete(value);
  } else {
    set.add(value);
  }
  return [...set];
};
